<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import DsModal from 'JIX/components/ds/DsModal.vue';
import CompanyRatingStars from 'JIX/components/CompanyRatingStars.vue';
import DsRadioButton from 'JIX/components/ds/DsRadioButton.vue';
import { useFormValidation } from 'JIX/form_validation.js';
import { remainingTextInputField } from 'JIX/ui/remainingText.js';
import SvgIcon from 'JIX/components/SvgIcon.vue';
import { bsTooltip as vBsTooltip } from 'JIX/vue-bs-tooltip.js';
import DsIconButton from 'JIX/components/ds/DsIconButton.vue';
import bootbox from 'bootbox';

import 'icons/valid.svg';

import T from './CompanyRatingNew.i18n.js';

import { api } from 'JIX/utils.js';

const emit = defineEmits(['existing:rating']);

const maxCommentLength = 600;
const companyRatingNewModal = ref();

const props = defineProps({
    proxycompanyid: {
        type: Number,
        required: true,
        default: 0,
    },
});
const writePhase = ref(true);
const hadRating = ref(false);
const termsUrl = ref("/betingelser#eval");
const deleteUrl = ref("/api/company-rating/v1/delete-rating");
const fillUrl = ref("/api/company-rating/v1/current-rating");
const sendUrl = ref("/api/company-rating/v1/update-rating");
const response = ref("");
const positiveComment = ref();
const positiveCommentCounter = ref();
const negativeComment = ref();
const negativeCommentCounter = ref();

const { formEl, formData, wasValidated, validationErrors, reset, submit } = useFormValidation({
    onReset() {
        fillExisting();
    },
    async onSubmit() {
        let params = {
            questionnaireid: 1,
            proxycompanyid: props.proxycompanyid,
            still_employed: formData.stillEmployed,
            ...formData.ratings,
            comment_positive: formData.positiveComment,
            comment_negative: formData.negativeComment,
        };
        await api('POST', sendUrl.value, params).then(res => {
            response.value = res.message;
            emit('existing:rating', { existing: true });
        });
    },
});

resetFormData();

onMounted(() => {
    reset();
});

const nextStepReady = computed(() => getRating(1) !== 0);
const sendReady = computed(() =>
    nextStepReady.value &&
    ['-1', '0', '1'].includes(String(formData.stillEmployed)) &&
    String(formData.positiveComment).length <= maxCommentLength &&
    String(formData.negativeComment).length <= maxCommentLength
);

function resetFormData() {
    formData.questionnaireid = 1;
    formData.proxycompanyid = props.proxycompanyid;
    formData.stillEmployed = null;
    formData.ratings = { question_entry0: 0, question_entry1: 0, question_entry2: 0, question_entry3: 0, question_entry4: 0, question_entry5: 0 };
    formData.positiveComment = "";
    formData.negativeComment = "";
}

function setRating(category, rating) {
    formData.ratings['question_entry' + category] = rating;
}

function getRating(category) {
    return formData.ratings['question_entry' + category] || 0;
}

function fillExisting() {
    resetFormData();
    let params = {
        questionnaireid: 1,
        proxycompanyid: props.proxycompanyid,
    };
    api('GET', fillUrl.value, params)
        .catch(_ => {})
        .then(res => {
            if (res !== undefined) {
                formData.stillEmployed = String(res.stillEmployed);
                formData.positiveComment = res.positiveComment;
                formData.negativeComment = res.negativeComment;
                formData.ratings = res.ratings;
                hadRating.value = res.stillEmployed !== undefined;
            }
            emit('existing:rating', { existing: hadRating.value });
        });
}

function handleDeleteRating() {
    bootbox.dialog({
        title: T.confirmDeleteHeader(),
        message: T.confirmDeleteMessage(),
        onEscape: true,
        backdrop: true,
        buttons: {
            confirm: {
                label: T.deleteButton(),
                className: 'btn btn-primary',
                callback: function() {
                    const params = {
                        questionnaireid: 1,
                        proxycompanyid: props.proxycompanyid,
                    };
                    api('POST', deleteUrl.value, params).then(() => {
                        reset();
                        hadRating.value = false;
                    });
                }
            },
            cancel: {
                label: T.cancelButton(),
                className: 'btn btn-secondary'
            }
        }
    });
}

function show() {
    companyRatingNewModal.value.show();
}

function hide() {
    companyRatingNewModal.value.hide();
    setTimeout(() => {
        response.value = "";
        writePhase.value = true;
    }, 100);
}

defineExpose({ show });

async function setupCommentPhase() {
    writePhase.value = false;
    await nextTick();
    setupCounters();
}

function setupCounters() {
    setupCounter(positiveComment, positiveCommentCounter);
    setupCounter(negativeComment, negativeCommentCounter);
}

function setupCounter(field, counter) {
    const f = field.value;
    if (f) {
        const c = counter.value;
        remainingTextInputField(0, maxCommentLength, f, c);
    }
}

</script>

<template>
    <DsModal
        role="dialog"
        ref="companyRatingNewModal"
        :show="false"
    >
        <div
            v-if="response.length > 0"
            class="text-center"
        >
            <DsIconButton
                icon-name="valid"
                type="primary"
                label=""
            />
            <h3>
                {{ T.completeHeader() }}
            </h3>
            <p>
                {{ response }}
            </p>
            <button class="btn btn-sm btn-primary" data-dismiss="modal" @click="hide">
                {{ T.closeButton() }}
            </button>
        </div>
        <div v-else>
            <form ref="formEl" :class="{ 'was-validated': wasValidated }">
                <div class="d-flex mb-4">
                    <div :class="{ 'd-inline': true, 'mx-auto': true, 'text-muted': !writePhase }">
                        <div class="text-center mb-2">
                            <span :class="{ 'rounded-circle':true, 'text-lg':true, 'px-3':true,'py-2':true, 'text-negative':writePhase, 'text-primary':!writePhase, 'bg-primary':writePhase, 'bg-surface-1':!writePhase }">1</span>
                        </div>
                        {{ T.menuWrite() }}
                    </div>
                    <div :class="{ 'd-inline': true, 'mx-auto': true, 'text-muted': writePhase }">
                        <div class="text-center mb-2">
                            <span :class="{ 'rounded-circle':true, 'text-lg':true, 'px-3':true,'py-2':true, 'text-negative':!writePhase, 'text-primary': writePhase, 'bg-primary': !writePhase, 'bg-surface-1': writePhase }">2</span>
                        </div>
                        {{ T.menuSend() }}
                    </div>
                </div>
                <div v-if="writePhase">
                    <div class="d-flex flex-column flex-md-row">
                        <label class="control-label mb-0">
                            {{ T.writeOverall() }}
                        </label>
                        <CompanyRatingStars
                            class="ml-md-auto"
                            size="inline"
                            :rating="getRating(1)"
                            :interactive="true"
                            @selected="setRating(1, $event.rating)"
                        />
                    </div>
                    <hr class="my-4">
                    <h4>
                        {{ T.writeCategoryHeader() }}
                    </h4>
                    <div
                        v-for="category in [2,5,4,7,3,6]"
                        :key="category"
                        class="mb-3 d-flex flex-column flex-md-row"
                    >
                        <label class="control-label mb-0">
                            {{ T.categoryName(category) }}
                            <SvgIcon
                                v-bs-tooltip="T.categoryTooltip(category)"
                                name="tooltip"
                                size="inline"
                            />
                        </label>
                        <CompanyRatingStars
                            class="ml-md-auto"
                            :rating="getRating(category)"
                            :interactive="true"
                            size="inline"
                            @selected="setRating(category, $event.rating)"
                        />
                    </div>
                    <div class="d-flex mt-4">
                        <button
                            type="button"
                            data-dismiss="modal"
                            aria-label="Close"
                            class="btn btn-secondary btn-sm"
                            key="cancelBtn"
                        >
                            {{ T.cancelButton() }}
                        </button>
                        <button
                            type="button"
                            aria-label="Close"
                            class="btn btn-danger btn-sm ml-3"
                            key="deleteBtn"
                            @click="handleDeleteRating()"
                            v-if="hadRating"
                        >
                            {{ T.deleteButton() }}
                        </button>
                        <button
                            type="button"
                            :disabled="!nextStepReady"
                            class="btn btn-primary btn-sm ml-auto"
                            @click="setupCommentPhase()"
                            key="nextBtn"
                        >
                            {{ T.nextButton() }}
                        </button>
                    </div>
                </div>
                <div v-else>
                    <h4>
                        {{ T.commentHeader() }}
                    </h4>
                    <div class="form-group mb-3">
                        <label class="control-label" for="positive-comment">
                            {{ T.writePositive() }}.
                            <span id="positive-comment-counter" ref="positiveCommentCounter" />
                        </label>
                        <textarea
                            class="form-control"
                            v-model="formData.positiveComment"
                            id="positive-comment"
                            ref="positiveComment"
                            name="positive-comment"
                            :class="{ 'is-invalid': validationErrors.comment_positive }"
                        />
                        <div class="invalid-feedback">
                            {{ validationErrors.comment_positive }}
                        </div>
                    </div>
                    <div class="form-group">
                        <label class="control-label" for="negative-comment">
                            {{ T.writeNegative() }}.
                            <span id="negative-comment-counter" ref="negativeCommentCounter" />
                        </label>
                        <textarea
                            class="form-control"
                            v-model="formData.negativeComment"
                            id="negative-comment"
                            ref="negativeComment"
                            name="negative-comment"
                            :class="{ 'is-invalid': validationErrors.comment_negative }"
                        />
                        <div class="invalid-feedback">
                            {{ validationErrors.comment_negative }}
                        </div>
                    </div>
                    <h4>
                        {{ T.statusHeader() }}
                    </h4>
                    <div class="form-group">
                        <DsRadioButton
                            v-for="status in ['0','1','-1']"
                            :key="status"
                            v-model="formData.stillEmployed"
                            name="active-status"
                            :id="'active-status'+status"
                            :value="status"
                        >
                            {{ T.statusName(status) }}
                        </DsRadioButton>
                    </div>
                    <div class="text-sm">
                        {{ T.termsText() }}
                        <a :href="termsUrl">
                            {{ T.termsUrlText() }}
                        </a>
                    </div>
                    <div class="d-flex mt-4">
                        <button
                            type="button"
                            @click="writePhase=true"
                            class="btn btn-secondary btn-sm"
                            key="backBtn"
                        >
                            {{ T.backButton() }}
                        </button>
                        <button
                            type="button"
                            :disabled="!sendReady"
                            @click="submit()"
                            class="btn btn-primary btn-sm ml-auto"
                            key="submitBtn"
                        >
                            {{ T.sendButton() }}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </DsModal>
</template>
